import React, { useEffect, useState } from 'react';
import { trackPixel, trackAndNavigate } from '../utils/pixel';
const PLAN_VALUES: Record<string, number> = {
  megapack: 22950,
  'megapack-gestion': 29950,
  'megapack-pro': 34950
};
type Plan = 'megapack' | 'megapack-gestion' | 'megapack-pro';
export function Pricing() {
  useEffect(() => {
    const section = document.getElementById('pricing-section');
    if (!section) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        trackPixel('ViewContent', {
          content_name: 'Pricing Section',
          content_category: 'Nutrición Resuelta'
        });
        observer.disconnect();
      }
    }, {
      threshold: 0.3
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);
  const [selected, setSelected] = useState<Plan>('megapack-pro');
  const cartUrls: Record<Plan, string> = {
    megapack: 'https://www.bibliotecaprofesional.com/cart/48299489689770:1',
    'megapack-gestion': 'https://www.bibliotecaprofesional.com/cart/48295641645226:1',
    'megapack-pro': 'https://www.bibliotecaprofesional.com/cart/48388702732458:1'
  };
  const plans: Record<Plan, {
    emoji: string;
    name: string;
    desc: string;
    priceOld: string;
    priceNew: string;
    priceColor: string;
    cta: string;
    sub: string;
    badge?: string;
    badgeColor?: string;
  }> = {
    megapack: {
      emoji: '📗',
      name: 'MEGAPACK BASE',
      desc: '6 tomos + 6 bonos · Sistema Clínico Completo',
      priceOld: '$58.990',
      priceNew: '$22.950',
      priceColor: 'text-gray-900',
      cta: '→ SÍ, QUIERO EL MEGAPACK BASE',
      sub: '6 tomos · 6 bonos · Acceso inmediato'
    },
    'megapack-gestion': {
      emoji: '📋',
      name: 'MEGAPACK GESTIÓN COMPLETA',
      desc: 'Todo el Base + 6 Planillas Profesionales listas para tu consultorio.',
      priceOld: '$79.990',
      priceNew: '$29.950',
      priceColor: 'text-gray-900',
      cta: '→ SÍ, QUIERO EL MEGAPACK GESTIÓN COMPLETA',
      sub: '6 tomos · 6 bonos · 6 Planillas Profesionales · Acceso inmediato',
      badge: '⭐ OFERTA 2',
      badgeColor: 'bg-gray-700'
    },
    'megapack-pro': {
      emoji: '🏆',
      name: 'MEGAPACK CONSULTORIO PROFESIONAL',
      desc: 'Todo el Gestión Completa + Planes por patología + Recetas listas + Láminas visuales educativas.',
      priceOld: '$102.990',
      priceNew: '$34.950',
      priceColor: 'text-green-700',
      cta: '→ SÍ, QUIERO EL MEGAPACK CONSULTORIO PROFESIONAL',
      sub: '6 tomos · 6 bonos · Planillas · Planes por patología · Recetas · Láminas · Acceso inmediato',
      badge: '🏆 MÁS ELEGIDO',
      badgeColor: 'bg-green-700'
    }
  };
  const active = plans[selected];
  return <section id="pricing-section" className="w-full px-4 sm:px-6 lg:px-8 py-12 text-gray-900" style={{
    backgroundImage: 'linear-gradient(180deg, rgb(254,242,242) 0%, rgb(248,250,252) 8%, rgb(248,250,252) 85%, rgb(240,253,244) 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
  }} data-id="element-206">
      <div className="mx-auto max-w-3xl" data-id="element-207">
        {/* Heading */}
        <div className="text-center mb-8" data-id="element-208">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-wide rounded-full px-4 py-1.5 mb-4" style={{
          color: 'rgb(21,128,61)',
          border: '1px solid rgba(34,197,94,0.40)',
          backgroundColor: 'rgba(34,197,94,0.05)'
        }} data-id="element-209">
            🔥 Precio de lanzamiento — Sube sin previo aviso
          </div>
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 m-0 mb-2" data-id="element-210">
            Elegí tu <span className="text-green-700" data-id="element-211">Megapack:</span>
          </h2>
          <p className="text-xs text-gray-500 m-0" data-id="element-212">
            Las tres opciones incluyen los 6 bonos estratégicos completos
          </p>
        </div>

        {/* Plan Selector */}
        <div className="flex flex-col gap-3 mb-6" data-id="element-213">
          {(Object.keys(plans) as Plan[]).map(plan => {
          const p = plans[plan];
          const isSelected = selected === plan;
          const isPro = plan === 'megapack-pro';
          return <button key={plan} onClick={() => {
            setSelected(plan);
            trackPixel('AddToCart', {
              content_name: p.name,
              content_ids: [plan],
              value: PLAN_VALUES[plan],
              currency: 'ARS'
            });
          }} className="w-full text-left rounded-xl p-4 transition-all cursor-pointer" style={{
            backgroundColor: isSelected ? 'rgba(34,197,94,0.04)' : 'rgb(255,255,255)',
            border: isPro ? isSelected ? '2px solid rgb(21,128,61)' : '2px solid rgba(34,197,94,0.40)' : isSelected ? '2px solid rgb(21,128,61)' : '1px solid rgb(229,231,235)',
            boxShadow: isSelected ? 'rgba(34,197,94,0.15) 0px 0px 30px 0px' : isPro ? 'rgba(34,197,94,0.08) 0px 4px 16px 0px' : undefined
          }} data-id="element-214">
                <div className="flex items-center gap-3" data-id="element-215">
                  {/* Radio */}
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2" style={{
                borderColor: isSelected ? 'rgb(21,128,61)' : 'rgb(209,213,219)'
              }} data-id="element-216">
                    {isSelected && <div className="h-2.5 w-2.5 rounded-full" style={{
                  backgroundColor: 'rgb(21,128,61)'
                }} data-id="element-217" />}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0" data-id="element-218">
                    <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-219">
                      <span data-id="element-220">{p.emoji}</span>
                      <span className="text-sm font-black text-gray-900" data-id="element-221">
                        {p.name}
                      </span>
                      {p.badge && <span className={`${p.badgeColor} text-[10px] font-black text-white rounded-full px-2 py-0.5`} data-id="element-222">
                          {p.badge}
                        </span>}
                    </div>
                    <p className="text-xs text-gray-500 m-0" data-id="element-223">{p.desc}</p>
                  </div>

                  {/* Price */}
                  <div className="shrink-0 text-right" data-id="element-224">
                    <div className="text-xs text-gray-400 line-through" data-id="element-225">
                      {p.priceOld}
                    </div>
                    <div className={`text-2xl font-black ${p.priceColor}`} data-id="element-226">
                      {p.priceNew}
                    </div>
                  </div>
                </div>
              </button>;
        })}
        </div>

        {/* CTA Block */}
        <div className="rounded-2xl p-5 shadow-lg" style={{
        background: 'linear-gradient(135deg, rgb(15,100,50) 0%, rgb(21,128,61) 100%)'
      }} data-id="element-227">
          {/* What you get summary */}
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mb-4" data-id="element-228">
            {active.sub.split(' · ').map(item => <span key={item} className="text-[11px] font-bold text-white/90 flex items-center gap-1" data-id="element-229">
                <span className="text-white/60" data-id="element-230">✓</span> {item}
              </span>)}
          </div>

          {/* Main CTA */}
          <a href={cartUrls[selected]} onClick={e => {
          e.preventDefault();
          trackAndNavigate('InitiateCheckout', {
            content_name: selected === 'megapack-pro' ? 'Megapack Consultorio Profesional' : selected === 'megapack-gestion' ? 'Megapack Gestión Completa' : 'Megapack Base',
            content_category: 'Nutrición Resuelta',
            value: selected === 'megapack-pro' ? 34950 : selected === 'megapack-gestion' ? 29950 : 22950,
            currency: 'ARS'
          }, cartUrls[selected]);
        }} className="block w-full bg-white text-center text-sm sm:text-base font-black rounded-xl py-4 no-underline transition-colors hover:bg-green-50" style={{
          color: 'rgb(21,128,61)'
        }} data-id="element-231">
            {active.cta}
          </a>

          {/* Subline */}
          <p className="text-center text-[11px] text-white/70 mt-3 mb-0" data-id="element-232">
            Más de 500 nutricionistas ya trabajan con criterio y cobran lo que
            merecen
          </p>
        </div>

        {/* Trust Badges */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs text-gray-500 bg-white rounded-xl px-4 py-3 shadow-sm border border-gray-100" data-id="element-233">
          <span data-id="element-234">🔒 Pago seguro</span>
          <span data-id="element-235">⚡ Acceso en 5 min</span>
          <span data-id="element-236">🛡️ Garantía 7 días</span>
          <span data-id="element-237">💳 Mercado Pago</span>
        </div>
      </div>
    </section>;
}